import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded'
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import useDebounce from 'utils/hooks/useDebounce'
import SearchableSelect from 'components/SearchableSelect'
import { SkeletonRows } from 'components/Skeleton'
import { ROLE_OPTIONS, roleLabel } from 'utils/roleOptions'
import { pathOr } from 'ramda'
import { ink, line, radius, surface, font, transition } from 'features/dashboard/tokens'
import {
  fetchAuthUserAccessRequestsRoutine,
  searchInAllBpsRoutine, clearSearchedBpsListRoutine, requestAccessToBpRoutine,
  searchEstatesToRequestRoutine, clearSearchedEstatesRoutine, requestAccessToEstateRoutine
} from 'features/AccessRequest/ducks/actions'
import {
  selectSearchedAccessRequestBps, selectPendingRequests,
  selectPendingEstateRequests, selectSearchedEstates
} from 'features/AccessRequest/ducks/selectors'
import { fetchBpUnitsForAccessRequestRoutine, requestAccessToUnitRoutine } from 'ducks/units/actions'
import { selectBpUnits } from 'ducks/units/selectors'

const RequestModal = ({ target, onClose, onSubmit }) => {
  const dispatch = useDispatch()
  const units = useSelector(selectBpUnits)
  const [role, setRole] = useState('agent')
  const [consent, setConsent] = useState(false)
  const [unitRequest, setUnitRequest] = useState(false)
  const [unitId, setUnitId] = useState(null)

  useEffect(() => {
    if (target.kind === 'building') dispatch(fetchBpUnitsForAccessRequestRoutine({ bpId: target.id }))
  }, [target.id, target.kind, dispatch])

  const hasUnits = target.kind === 'building' && units.length > 0
  const roleOpts = [
    ...ROLE_OPTIONS.map(({ value, label }) => ({ value, label })),
    ...(unitRequest ? [{ value: 'occupant', label: 'Occupant' }] : [])
  ]
  const unitOpts = units.map(unit => ({ value: unit.id, label: unit.name }))
  const kindWord = target.kind === 'estate' ? 'estate' : 'building'
  const canSend = !!role && consent && (!unitRequest || !!unitId)

  const toggleUnitRequest = () => {
    setUnitRequest(value => {
      const next = !value
      if (!next && role === 'occupant') setRole('agent')
      if (!next) setUnitId(null)
      return next
    })
  }

  return createPortal(
    <Backdrop onClick={onClose}>
      <Card onClick={event => event.stopPropagation()} role='dialog' aria-modal='true'>
        <CardTitle>Request access to {unitRequest && unitId ? (unitOpts.find(u => u.value === unitId)?.label || 'a unit') : target.name}</CardTitle>
        {target.address && <CardSub>{target.address}</CardSub>}
        <CardBody>
          {hasUnits && (
            <ConsentRow type='button' onClick={toggleUnitRequest} $on={unitRequest}>
              <Check $on={unitRequest}>{unitRequest && '✓'}</Check>
              <ConsentText>Request access to a specific unit instead of the whole Building Passport.</ConsentText>
            </ConsentRow>
          )}
          {hasUnits && unitRequest && (
            <div>
              <FieldLabel>Which unit?</FieldLabel>
              <SearchableSelect ariaLabel='Unit' value={unitId} placeholder='Select a unit' options={unitOpts} onChange={setUnitId} />
            </div>
          )}
          <FieldLabel>What role do you need?</FieldLabel>
          <div data-tour-id='accessRequest.role'>
            <SearchableSelect
              ariaLabel='Role'
              value={role}
              options={roleOpts}
              onChange={setRole}
            />
          </div>
          <ConsentRow type='button' data-tour-id='accessRequest.consent' onClick={() => setConsent(value => !value)} $on={consent}>
            <Check $on={consent}>{consent && '✓'}</Check>
            <ConsentText>
              I understand the {kindWord} managers will see this request and my email address, so they can contact me.
            </ConsentText>
          </ConsentRow>
        </CardBody>
        <CardActions>
          <Ghost type='button' onClick={onClose}>Cancel</Ghost>
          <Primary type='button' data-tour-id='accessRequest.send' disabled={!canSend} onClick={() => onSubmit({ relationType: role, unitId: unitRequest ? unitId : null })}>Send request</Primary>
        </CardActions>
      </Card>
    </Backdrop>,
    document.body
  )
}

const AccessRequest = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [scope, setScope] = useState('building')
  const [query, setQuery] = useState('')
  const debounced = useDebounce(query, 450)
  const [searching, setSearching] = useState(false)
  const [target, setTarget] = useState(null)
  const [requestedEstateIds, setRequestedEstateIds] = useState(() => new Set())

  const bpResults = useSelector(selectSearchedAccessRequestBps)
  const estateResults = useSelector(selectSearchedEstates)
  const pendingBp = useSelector(selectPendingRequests)
  const pendingEstates = useSelector(selectPendingEstateRequests)

  useEffect(() => { dispatch(fetchAuthUserAccessRequestsRoutine()) }, [])

  useEffect(() => {
    const clean = debounced.trim()
    if (clean.length >= 3) {
      setSearching(true)
      if (scope === 'building') dispatch(searchInAllBpsRoutine({ query: clean }))
      else dispatch(searchEstatesToRequestRoutine({ query: clean }))
    } else {
      setSearching(false)
      dispatch(clearSearchedBpsListRoutine())
      dispatch(clearSearchedEstatesRoutine())
    }
  }, [debounced, scope])

  useEffect(() => { setSearching(false) }, [bpResults, estateResults])

  const switchScope = next => {
    if (next === scope) return
    setScope(next)
    setQuery('')
    setSearching(false)
    dispatch(clearSearchedBpsListRoutine())
    dispatch(clearSearchedEstatesRoutine())
  }

  const openRequest = (kind, item) => setTarget({
    kind,
    id: item.id,
    name: item.name,
    address: [item.street, item.city, item.postalCode].filter(Boolean).join(', ')
  })

  const submitRequest = ({ relationType, unitId }) => {
    if (!target) return
    if (target.kind === 'building') {
      if (unitId) {
        dispatch(requestAccessToUnitRoutine({ bpId: target.id, unitId, relationType }))
      } else {
        dispatch(requestAccessToBpRoutine({ bpId: target.id, relationType }))
      }
    } else {
      dispatch(requestAccessToEstateRoutine({ id: target.id, relationType }))
      setRequestedEstateIds(prev => new Set(prev).add(target.id))
    }
    setTarget(null)
  }

  const pendingEstateItems = useMemo(() => pendingEstates.map(request => ({
    id: request.id,
    name: pathOr('This estate', ['estate', 'name'], request),
    role: request.relationType,
    kind: 'estate'
  })), [pendingEstates])

  const pendingBpItems = useMemo(() => pendingBp.map(request => {
    const isUnit = request.buildingUnitPassportId && request.buildingUnitPassport
    return {
      id: request.id,
      name: isUnit
        ? pathOr('This unit', ['buildingUnitPassport', 'name'], request)
        : pathOr('This Building Passport', ['buildingPassport', 'name'], request),
      role: request.relationType,
      kind: isUnit ? 'unit' : 'building'
    }
  }), [pendingBp])

  const pending = [...pendingBpItems, ...pendingEstateItems]
  const enoughChars = debounced.trim().length >= 3
  const results = scope === 'building' ? bpResults : estateResults

  return (
    <Page>
      <Header>
        <H1>Request access</H1>
        <Sub>Find a Building Passport or estate and ask its managers to let you in.</Sub>
      </Header>

      <Toggle role='tablist'>
        <TBtn type='button' role='tab' aria-selected={scope === 'building'} $on={scope === 'building'} onClick={() => switchScope('building')}>
          <HomeWorkRoundedIcon style={{ fontSize: 17 }} /> Building Passports
        </TBtn>
        <TBtn type='button' role='tab' aria-selected={scope === 'estate'} $on={scope === 'estate'} onClick={() => switchScope('estate')}>
          <ApartmentRoundedIcon style={{ fontSize: 17 }} /> Estates
        </TBtn>
      </Toggle>

      <SearchBar>
        <SearchRoundedIcon style={{ fontSize: 21, color: ink.mute }} />
        <SearchInput
          data-tour-id='accessRequest.search'
          placeholder={scope === 'building' ? 'Search Building Passports by name or address…' : 'Search estates in your organisation…'}
          value={query}
          onChange={event => setQuery(event.target.value)}
          aria-label={scope === 'building' ? 'Search Building Passports' : 'Search estates'}
        />
        {query && <ClearBtn type='button' aria-label='Clear search' onClick={() => setQuery('')}><CloseRoundedIcon style={{ fontSize: 18 }} /></ClearBtn>}
      </SearchBar>

      {!enoughChars ? (
        <Hint>Type at least 3 characters to search {scope === 'building' ? 'Building Passports' : 'estates'}.</Hint>
      ) : searching ? (
        <SkeletonRows rows={4} gap={10} widths={['100%', '100%', '100%', '100%']} />
      ) : results.length === 0 ? (
        <Empty>No {scope === 'building' ? 'Building Passports' : 'estates'} found for “{debounced.trim()}”.</Empty>
      ) : (
        <>
          <CountLine>{results.length} {scope === 'building' ? (results.length === 1 ? 'Building Passport' : 'Building Passports') : (results.length === 1 ? 'estate' : 'estates')} found</CountLine>
          <Grid>
            {scope === 'building' && bpResults.map((bp, index) => (
              <ResultCard key={bp.id}>
                <Tile $building><HomeWorkRoundedIcon style={{ fontSize: 20 }} /></Tile>
                <Info>
                  <Name title={bp.name}>{bp.name}</Name>
                  <Meta>{[bp.street, bp.city, bp.postalCode].filter(Boolean).join(', ') || 'No address on file'}</Meta>
                </Info>
                <RequestBtn type='button' data-tour-id={index === 0 ? 'accessRequest.first-request' : undefined} onClick={() => openRequest('building', bp)}>Request access</RequestBtn>
              </ResultCard>
            ))}
            {scope === 'estate' && estateResults.map(estate => {
              const pendingState = estate.hasPendingRequest || requestedEstateIds.has(estate.id)
              return (
                <ResultCard key={estate.id}>
                  <Tile><ApartmentRoundedIcon style={{ fontSize: 20 }} /></Tile>
                  <Info>
                    <Name title={estate.name}>{estate.name}</Name>
                    <Meta>
                      {(estate.bpCount ?? 0)} {estate.bpCount === 1 ? 'Building Passport' : 'Building Passports'}
                      {estate.portfolio ? ` · ${estate.portfolio.name}` : ''}
                    </Meta>
                  </Info>
                  {estate.isMember ? (
                    <OpenBtn type='button' onClick={() => navigate(`/estates/${estate.id}`)}>Open <ArrowForwardRoundedIcon style={{ fontSize: 15 }} /></OpenBtn>
                  ) : pendingState ? (
                    <StatusPill>Awaiting approval</StatusPill>
                  ) : (
                    <RequestBtn type='button' onClick={() => openRequest('estate', estate)}>Request access</RequestBtn>
                  )}
                </ResultCard>
              )
            })}
          </Grid>
        </>
      )}

      {pending.length > 0 && (
        <PendingSection>
          <SectionTitle>Your pending requests</SectionTitle>
          <Grid>
            {pending.map(item => (
              <ResultCard key={`${item.kind}-${item.id}`} $muted>
                <Tile $building={item.kind !== 'estate'}>
                  {item.kind === 'estate' ? <ApartmentRoundedIcon style={{ fontSize: 20 }} /> : <HomeWorkRoundedIcon style={{ fontSize: 20 }} />}
                </Tile>
                <Info>
                  <Name title={item.name}>{item.name}</Name>
                  <Meta>Requested as {roleLabel(item.role)}</Meta>
                </Info>
                <StatusPill>Awaiting approval</StatusPill>
              </ResultCard>
            ))}
          </Grid>
        </PendingSection>
      )}

      {target && <RequestModal target={target} onClose={() => setTarget(null)} onSubmit={submitRequest} />}
    </Page>
  )
}

export default AccessRequest

const Page = styled.div`max-width: 860px; margin: 0 auto; padding: 8px 2px 40px;`
const Header = styled.div`margin-bottom: 20px;`
const H1 = styled.h1`margin: 0; font-size: ${font.title}; font-weight: 800; color: ${ink.base};`
const Sub = styled.div`margin-top: 4px; font-size: ${font.body}; color: ${ink.mute};`

const Toggle = styled.div`display: inline-flex; gap: 4px; padding: 4px; background: ${surface.wash}; border: 1px solid ${line.base}; border-radius: ${radius.md}; margin-bottom: 16px;
  @media (max-width: 768px) { display: flex; }`
const TBtn = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: 7px; flex: 1;
  padding: 9px 16px; border: none; border-radius: ${radius.sm}; cursor: pointer; white-space: nowrap;
  font-size: ${font.body}; font-weight: 700; transition: ${transition};
  background: ${({ $on }) => ($on ? surface.card : 'transparent')};
  color: ${({ $on, theme }) => ($on ? theme.colors.secondary : ink.soft)};
  box-shadow: ${({ $on }) => ($on ? '0 1px 2px rgba(15,23,42,0.08)' : 'none')};
`

const SearchBar = styled.div`
  display: flex; align-items: center; gap: 10px; padding: 12px 16px;
  background: ${surface.card}; border: 1px solid ${line.strong}; border-radius: ${radius.md};
  margin-bottom: 18px;
  &:focus-within { border-color: ${({ theme }) => theme.colors.secondary}; }
`
const SearchInput = styled.input`
  flex: 1; min-width: 0; border: none; outline: none; background: transparent;
  font-size: 15px; color: ${ink.base};
  &::placeholder { color: ${ink.mute}; }
`
const ClearBtn = styled.button`
  flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; width: 26px; height: 26px;
  border: none; background: transparent; color: ${ink.mute}; cursor: pointer; border-radius: ${radius.sm}; transition: ${transition};
  &:hover { background: ${surface.wash}; color: ${ink.base}; }
`

const Hint = styled.div`font-size: ${font.small}; color: ${ink.mute}; padding: 8px 2px;`
const Empty = styled.div`font-size: ${font.body}; color: ${ink.mute}; padding: 16px 2px;`
const CountLine = styled.div`font-size: ${font.small}; font-weight: 600; color: ${({ theme }) => theme.colors.secondary}; margin-bottom: 10px;`

const Grid = styled.div`display: flex; flex-direction: column; gap: 10px;`
const ResultCard = styled.div`
  display: flex; align-items: center; gap: 12px; padding: 12px 14px;
  background: ${({ $muted }) => ($muted ? surface.wash : surface.card)};
  border: 1px solid ${line.base}; border-radius: ${radius.sm};
  @media (max-width: 768px) { flex-wrap: wrap; }
`
const Tile = styled.span`
  flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px; border-radius: ${radius.sm};
  background: ${({ $building, theme }) => ($building ? surface.wash : `${theme.colors.secondary}1a`)};
  color: ${({ $building, theme }) => ($building ? ink.mute : theme.colors.secondary)};
`
const Info = styled.div`flex: 1; min-width: 0;`
const Name = styled.div`font-size: ${font.emphasis}; font-weight: 700; color: ${ink.base}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`
const Meta = styled.div`margin-top: 2px; font-size: ${font.small}; color: ${ink.mute}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`

const RequestBtn = styled.button`
  flex-shrink: 0; display: inline-flex; align-items: center; padding: 8px 15px; border: none; border-radius: ${radius.sm};
  background: ${({ theme }) => theme.colors.secondary}; color: #fff; font-size: 13px; font-weight: 700; cursor: pointer; transition: ${transition};
  &:hover { filter: brightness(1.05); }
  @media (max-width: 768px) { flex-basis: 100%; justify-content: center; margin-top: 4px; }
`
const OpenBtn = styled.button`
  flex-shrink: 0; display: inline-flex; align-items: center; gap: 4px; padding: 8px 14px; border-radius: ${radius.sm};
  border: 1px solid ${line.strong}; background: ${surface.card}; color: ${ink.soft}; font-size: 13px; font-weight: 700; cursor: pointer; transition: ${transition};
  &:hover { border-color: ${({ theme }) => theme.colors.secondary}; color: ${({ theme }) => theme.colors.secondary}; }
`
const StatusPill = styled.span`
  flex-shrink: 0; padding: 6px 12px; border-radius: ${radius.pill}; background: #fef3c7; color: #d97706; font-size: 12px; font-weight: 700; white-space: nowrap;
`

const PendingSection = styled.div`margin-top: 34px;`
const SectionTitle = styled.h2`
  margin: 0 0 12px; font-size: ${font.emphasis}; font-weight: 800; color: ${ink.base};
  padding-bottom: 8px; border-bottom: 1px solid ${line.base};
`

const Backdrop = styled.div`
  position: fixed; inset: 0; z-index: 100050; background: rgba(12, 18, 41, 0.45);
  display: flex; align-items: center; justify-content: center; padding: 20px;
`
const Card = styled.div`background: ${surface.card}; border-radius: 14px; width: 100%; max-width: 440px; box-shadow: 0 20px 60px rgba(0,0,0,0.25);`
const CardTitle = styled.h2`margin: 0; padding: 20px 20px 2px; font-size: 17px; font-weight: 800; color: ${ink.base};`
const CardSub = styled.div`padding: 0 20px; font-size: ${font.caption}; color: ${ink.mute};`
const CardBody = styled.div`padding: 14px 20px; display: flex; flex-direction: column; gap: 12px;`
const FieldLabel = styled.label`font-size: ${font.small}; font-weight: 700; color: ${ink.soft}; text-transform: uppercase; letter-spacing: 0.03em;`
const ConsentRow = styled.button`
  display: flex; align-items: flex-start; gap: 10px; text-align: left; width: 100%; padding: 10px 12px; cursor: pointer;
  border: 1px solid ${({ $on, theme }) => ($on ? theme.colors.secondary : line.strong)}; border-radius: ${radius.sm}; background: ${surface.card};
`
const Check = styled.span`
  flex-shrink: 0; width: 18px; height: 18px; border-radius: 5px; display: inline-flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 800; color: #fff;
  border: 2px solid ${({ $on, theme }) => ($on ? theme.colors.secondary : line.strong)};
  background: ${({ $on, theme }) => ($on ? theme.colors.secondary : 'transparent')};
`
const ConsentText = styled.span`font-size: 12.5px; line-height: 1.45; color: ${ink.soft};`
const CardActions = styled.div`display: flex; justify-content: flex-end; gap: 10px; padding: 12px 20px 18px;`
const Ghost = styled.button`
  padding: 9px 16px; border: 1px solid ${line.strong}; background: ${surface.card}; border-radius: ${radius.sm}; cursor: pointer;
  font-size: ${font.body}; font-weight: 600; color: ${ink.soft};
`
const Primary = styled.button`
  padding: 9px 18px; border: none; border-radius: ${radius.sm}; cursor: pointer; font-size: ${font.body}; font-weight: 700; color: #fff;
  background: ${({ theme }) => theme.colors.secondary};
  &:hover:not(:disabled) { filter: brightness(1.05); }
  &:disabled { opacity: 0.5; cursor: default; }
`
